/**
 * Service Provider CoA Mappings View — server-mode DataTable.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { type ColumnDef, type ColumnFiltersState } from "@tanstack/react-table";
import { Card } from "@/ui/atoms/Card";
import { Loader, Button } from "@/ui/atoms";
import { DataTable } from "@/ui/molecules";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import { buildRowCount } from "@/useCases/rowCount";
import { ArrowLeft } from "lucide-react";

export type TCoALevel = "A" | "P" | "C";

export type TCoAMappingView = {
    id: string;
    thirdAccountCode: string;
    thirdAccountName: string;
    sourceSystem: string | null;
    yardiAccountId: string;
    yardiAccountCode: string | null;
    yardiAccountName: string | null;
    level: TCoALevel;
    isInForce: boolean;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export type TServiceProviderBasicInfo = {
    id: string;
    name: string;
    code: string;
};

export type TSpCoaQuery = {
    page: number;
    page_size: number;
    source_account_code?: string;
    source_account_name?: string;
    source_system?: string;
    level?: TCoALevel;
    yardi_account_code?: string;
    yardi_account_name?: string;
    sort_by?: string;
    sort_dir?: "asc" | "desc";
};

export type TViewServiceProviderCoAMappingsParams = {
    useCases: () => {
        onLoadServiceProvider: (
            serviceProviderId: string,
        ) => Promise<TServiceProviderBasicInfo | null>;
        onLoadCoAMappingsPage: (
            serviceProviderId: string,
            query: TSpCoaQuery,
            signal?: AbortSignal,
        ) => Promise<{ items: TCoAMappingView[]; total: number; totalUnfiltered: number }>;
    };
};

export type TViewServiceProviderCoAMappingsProps = Record<string, never>;

const PAGE_SIZE = 50;

const levelLabels: Record<TCoALevel, string> = { A: "Asset", P: "Property", C: "Company" };
const levelColors: Record<TCoALevel, string> = {
    A: "bg-blue-100 text-blue-700",
    P: "bg-green-100 text-green-700",
    C: "bg-purple-100 text-purple-700",
};
const LEVEL_OPTIONS = [
    { value: "A", label: "Asset" },
    { value: "P", label: "Property" },
    { value: "C", label: "Company" },
];

const firstString = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;

const toQuery = (input: TServerTableQueryInput): TSpCoaQuery => {
    const get = (id: string) => input.columnFilters.find((f) => f.id === id)?.value;
    const sort = input.sorting[0];
    const level = firstString(get("level"));
    return {
        page: input.page,
        page_size: input.pageSize,
        source_account_code: firstString(get("thirdAccountCode")),
        source_account_name: firstString(get("thirdAccountName")),
        source_system: firstString(get("sourceSystem")),
        level: level === "A" || level === "P" || level === "C" ? level : undefined,
        yardi_account_code: firstString(get("yardiAccountCode")),
        yardi_account_name: firstString(get("yardiAccountName")),
        sort_by: sort?.id,
        sort_dir: sort ? (sort.desc ? "desc" : "asc") : undefined,
    };
};

export const createViewServiceProviderCoAMappings = ({
    useCases,
}: TViewServiceProviderCoAMappingsParams) => {
    return function ViewServiceProviderCoAMappings() {
        const { serviceProviderId } = useParams<{ serviceProviderId: string }>();
        const navigate = useNavigate();
        const { onLoadCoAMappingsPage, onLoadServiceProvider } = useCases();

        const [serviceProvider, setServiceProvider] = useState<TServiceProviderBasicInfo | null>(
            null,
        );
        const [items, setItems] = useState<TCoAMappingView[]>([]);
        const [total, setTotal] = useState(0);
        const [totalUnfiltered, setTotalUnfiltered] = useState(0);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);

        useEffect(() => {
            if (!serviceProviderId) {
                setError("Service provider ID is required");
                setIsLoading(false);
                return;
            }
            void onLoadServiceProvider(serviceProviderId).then((sp) => {
                if (!sp) setError("Service provider not found");
                else setServiceProvider(sp);
            });
        }, [serviceProviderId, onLoadServiceProvider]);

        const fetchData = useCallback(
            async (query: TSpCoaQuery, signal?: AbortSignal) => {
                if (!serviceProviderId) return;
                setIsLoading(true);
                setError(null);
                try {
                    const data = await onLoadCoAMappingsPage(serviceProviderId, query, signal);
                    setItems(data.items);
                    setTotal(data.total);
                    setTotalUnfiltered(data.totalUnfiltered);
                    setIsLoading(false);
                } catch (err) {
                    // Swallow abort errors — the superseding request owns the loading state
                    // (including isLoading, which the newer in-flight request controls).
                    if (err instanceof DOMException && err.name === "AbortError") return;
                    setError(err instanceof Error ? err.message : "Failed to load data");
                    setIsLoading(false);
                }
            },
            [serviceProviderId, onLoadCoAMappingsPage],
        );

        const {
            onColumnFiltersChange,
            onSortingChange,
            sorting,
            setPage,
            page: currentPage,
            columnFilters,
        } = useServerTable<TSpCoaQuery>({ pageSize: PAGE_SIZE, toQuery, fetchData });

        const columns: ColumnDef<TCoAMappingView>[] = useMemo(
            () => [
                {
                    accessorKey: "thirdAccountCode",
                    id: "thirdAccountCode",
                    header: "Source Account #",
                    meta: { filterType: "text", filterPlaceholder: "Search source #…" },
                    cell: ({ row }) => (
                        <span className="font-mono text-sm">{row.original.thirdAccountCode}</span>
                    ),
                },
                {
                    accessorKey: "thirdAccountName",
                    id: "thirdAccountName",
                    header: "Source Account Name",
                    meta: { filterType: "text", filterPlaceholder: "Search source name…" },
                    cell: ({ row }) => (
                        <span className="text-sm">{row.original.thirdAccountName}</span>
                    ),
                },
                {
                    accessorKey: "sourceSystem",
                    id: "sourceSystem",
                    header: "Source System",
                    meta: { filterType: "text", filterPlaceholder: "Search source system…" },
                    cell: ({ row }) => (
                        <span className="text-sm">{row.original.sourceSystem || "—"}</span>
                    ),
                },
                {
                    accessorKey: "level",
                    id: "level",
                    header: "Propcode Level",
                    meta: { filterType: "select", filterOptions: LEVEL_OPTIONS },
                    cell: ({ row }) => {
                        const level = row.original.level;
                        return (
                            <span
                                className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-medium ${levelColors[level]}`}
                                title={levelLabels[level]}
                            >
                                {level}
                            </span>
                        );
                    },
                },
                {
                    accessorKey: "yardiAccountCode",
                    id: "yardiAccountCode",
                    header: "Account #",
                    meta: { filterType: "text", filterPlaceholder: "Search account #…" },
                    cell: ({ row }) => (
                        <span className="font-mono text-sm">
                            {row.original.yardiAccountCode || "—"}
                        </span>
                    ),
                },
                {
                    accessorKey: "yardiAccountName",
                    id: "yardiAccountName",
                    header: "Account Name",
                    meta: { filterType: "text", filterPlaceholder: "Search account name…" },
                    cell: ({ row }) => (
                        <span className="text-sm">{row.original.yardiAccountName || "—"}</span>
                    ),
                },
            ],
            [],
        );

        if (error) {
            return (
                <div className="text-center py-8">
                    <p className="text-destructive">{error}</p>
                </div>
            );
        }

        if (isLoading && items.length === 0 && !serviceProvider) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-semibold">
                        Chart of Account Mappings: {serviceProvider?.name}
                    </h1>
                    <Button
                        variant="outline"
                        onClick={() => navigate("/rev_apac/admin/business-partners")}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Business Partners
                    </Button>
                </div>

                <Card>
                    <DataTable
                        columns={columns}
                        data={items}
                        loading={isLoading}
                        manualFiltering
                        manualSorting
                        sortingState={sorting}
                        onSortingChange={onSortingChange}
                        onColumnFiltersChange={
                            onColumnFiltersChange as (f: ColumnFiltersState) => void
                        }
                        rowNoun={{ one: "mapping", many: "mappings" }}
                        rowCount={buildRowCount({
                            total,
                            totalUnfiltered,
                            hasActiveFilters: columnFilters.length > 0,
                        })}
                        pagination={{
                            page: currentPage,
                            pageSize: PAGE_SIZE,
                            total,
                            onPageChange: setPage,
                        }}
                        storageKey="sp-coa-mappings"
                        emptyMessage="No mappings match the current filters."
                    />
                </Card>
            </div>
        );
    };
};
